import { useCallback, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { computeTotals, DEFAULT_PAYMENT_METHODS, discountLabel, dueDateFor } from './format';
import { addInvoice, useInvoicingSession } from './invoicingStore';
import InvoicePageLayout from './InvoicePageLayout';
import InvoicePreview from './InvoicePreview';
import InvoiceSendPanel, { SaveAction } from './InvoiceSendPanel';
import { Client, InvoiceStatus, PaymentMethod, Sender } from './types';

interface InvoiceReviewProps {
    clients: Client[];
    sender: Sender;
}

const STATUS_BY_ACTION: Record<SaveAction, InvoiceStatus> = {
    send: 'unpaid',
    final: 'unpaid',
    link: 'unpaid',
    draft: 'draft',
    quote: 'draft',
};

const confirmation = (action: SaveAction, number: string, email: string) =>
    ({
        send: `Invoice ${number} sent to ${email}`,
        final: `Invoice ${number} saved`,
        link: `Invoice ${number} saved. Payment link created`,
        draft: `Invoice ${number} saved as a draft`,
        quote: `Quote ${number} sent to ${email}`,
    }[action]);

let sessionInvoiceCounter = 0;

/** The step after New Invoice → Save: the invoice as the client will see it, and how it can be paid. */
export default function InvoiceReview({ clients, sender }: Readonly<InvoiceReviewProps>) {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { draft } = useInvoicingSession();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(DEFAULT_PAYMENT_METHODS);

    const totals = draft ? computeTotals(draft.items, draft.discount, draft.discountMode) : null;
    const clientName = clients.find((c) => c.id === draft?.clientId)?.name ?? '';
    const due = draft ? dueDateFor(draft.date, draft.terms) : '';

    const handleBack = useCallback(() => history.push('/bookkeeping/invoicing/new'), [history]);
    const handleSave = useCallback(
        (action: SaveAction) => {
            if (!draft || !totals) {
                return;
            }
            sessionInvoiceCounter += 1;
            // Leave before the draft is cleared, so this page never renders its no-draft redirect.
            history.push('/bookkeeping/invoicing');
            addInvoice({
                id: `inv_session_${sessionInvoiceCounter}`,
                date: draft.date,
                due,
                number: draft.number,
                client: clientName,
                client_email: draft.clientEmail,
                amount: totals.total,
                status: STATUS_BY_ACTION[action],
                items: draft.items,
                discount: totals.discountAmount,
                discount_label: discountLabel(draft.discountMode, draft.discount, totals.discountAmount),
                message: draft.message,
                payment_methods: paymentMethods,
            });
            enqueueSnackbar(confirmation(action, draft.number, draft.clientEmail), { variant: 'success' });
        },
        [clientName, draft, due, enqueueSnackbar, history, paymentMethods, totals]
    );

    // Reached directly (a reload, or a bookmarked URL) there is nothing to review.
    if (!draft || !totals) {
        return <Redirect to="/bookkeeping/invoicing/new" />;
    }

    return (
        <InvoicePageLayout
            title="Review Invoice"
            onBack={handleBack}
            main={
                <InvoicePreview
                    sender={sender}
                    number={draft.number}
                    date={draft.date}
                    due={due}
                    clientName={clientName}
                    clientEmail={draft.clientEmail}
                    items={draft.items}
                    discountAmount={totals.discountAmount}
                    total={totals.total}
                    message={draft.message}
                />
            }
            side={
                <InvoiceSendPanel
                    number={draft.number}
                    subtotal={totals.subtotal}
                    discountLabel={discountLabel(draft.discountMode, draft.discount, totals.discountAmount)}
                    total={totals.total}
                    paymentMethods={paymentMethods}
                    onPaymentMethodsChange={setPaymentMethods}
                    onSave={handleSave}
                />
            }
        />
    );
}
